import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Avatar from '../Avatar'
import Button from '../Button'
import Icon from '../icon/Icon'
import Text from '../Text'

// Utils
import { useAppState } from '../../context/AppContext'

// Types
import { ICompanyMember } from '../../common/models/Company'

interface IProps {
  members: ICompanyMember[]
  segmentLabel?: string
  onSelectMember?: (member: ICompanyMember) => void
  onMemberHover?: (memberId: string | undefined) => void
  onClose: () => void
}

const InsightsMatrixListPopover: React.FC<IProps> = ({
  members,
  segmentLabel,
  onSelectMember,
  onMemberHover,
  onClose,
}) => {
  const { t } = useTranslation()
  const { theme } = useAppState()

  return (
    <Container>
      <Header>
        {segmentLabel ? (
          <Text textStyle="labelSmallCaps" color="textLight" tag="span">
            {`${t('insights.segment')}: `}
            <Text textStyle="labelSmallCaps" color="primary" tag="span">
              {segmentLabel}
            </Text>
          </Text>
        ) : (
          <Text textStyle="labelSmallCaps" color="textLight" tag="span">
            {t('insights.inThisSegment')}
          </Text>
        )}
        <CloseButton
          colorVariant="tertiary"
          icon="Close"
          iconSize={12}
          onClick={onClose}
        />
      </Header>
      <List>
        {members.map((member) => {
          const { firstName, preposition, lastName } = member
          return (
            <Row
              key={member._id}
              type="button"
              onClick={() => onSelectMember?.(member)}
              onMouseEnter={() => onMemberHover?.(member._id)}
              onMouseLeave={() => onMemberHover?.(undefined)}
              onFocus={() => onMemberHover?.(member._id)}
              onBlur={() => onMemberHover?.(undefined)}
            >
              <AvatarSlot>
                <Avatar user={member} />
              </AvatarSlot>
              <NameWrapper>
                <Text textStyle="labelHeavy" color="text" tag="span">
                  {firstName}
                </Text>
                {lastName && (
                  <Text textStyle="label" color="textLight" tag="span">
                    {preposition ? `${preposition} ${lastName}` : lastName}
                  </Text>
                )}
              </NameWrapper>
              <Icon
                type="ChevronRight"
                size={12}
                color={theme.colors.primary}
              />
            </Row>
          )
        })}
      </List>
    </Container>
  )
}

export default InsightsMatrixListPopover

const Container = styled.div`
  width: 320px;
  display: flex;
  flex-direction: column;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.textLighter};
`

const CloseButton = styled(Button)`
  margin-left: auto;
  padding: 8px;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 320px;
  overflow-y: auto;
  padding: 8px;
`

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border-radius: 8px;
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.15s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryFadeLight};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }

  > span:nth-of-type(1) {
    flex: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const NameWrapper = styled.span`
  flex: 1;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
`

const AvatarSlot = styled.div`
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  > figure {
    width: 100%;
    height: 100%;
    margin: 0;
  }
`
